// ============================================================================
// DealAnimation - hand-start signal built on framer-motion
// ============================================================================
// User-facing beat that fires every time gameState.handNumber increments.
// Two purposes:
//   1. Visual ack that a new hand has started, so the player isn't
//      surprised when their hole cards appear/refresh.
//   2. Prevents the "did my cards just change?" anxiety by showing a
//      clear "DEALING HAND #N" label during the 1.5s while the
//      playerCards/{uid} subscription catches up.
//
// Implementation: framer-motion handles the choreography (no custom CSS
// keyframes, no raw setTimeout for animation timing). Mounted in
// PremiumThemedTableSVG as a sibling overlay; self-removes via
// AnimatePresence when handNumber moves on or after the duration.
// ============================================================================
import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

interface DealAnimationProps {
  /** Increment this any time you want a fresh deal animation to play. */
  handNumber?: number;
  /** Epoch ms. While this is in the future the overlay is skipped entirely -
   *  used on tournament tables where the TOURNAMENT STARTING countdown owns
   *  the pre-hand beat and the NEW HAND burst would just cover it up. */
  suppressUntil?: number | null;
}

const TOTAL_MS = 1500;
const NUM_CARDS = 14; // pairs * 7-ish positions; enough to read as a deal

const DealAnimation: React.FC<DealAnimationProps> = ({ handNumber, suppressUntil }) => {
  const [visible, setVisible] = useState(false);
  // lastShown lives in a ref, NOT state. When it was state, setting it
  // re-ran this effect (it's a dep), whose cleanup cleared the hide timer
  // before the early-return path could re-arm it — leaving the DEALING
  // banner stuck on screen forever. A ref doesn't trigger re-runs, so the
  // timer survives until it fires or the hand actually changes.
  const lastShownRef = useRef<number | null>(null);

  useEffect(() => {
    if (typeof handNumber !== 'number' || handNumber <= 0) return;
    if (handNumber === lastShownRef.current) return;
    lastShownRef.current = handNumber;
    // Tournament pre-deal countdown active - skip the burst for this hand.
    if (suppressUntil && Date.now() < suppressUntil) return;
    setVisible(true);
  }, [handNumber, suppressUntil]);

  // Hide timer owns its own effect keyed on `visible`. This survives React
  // StrictMode's dev-mode double-invoke (run → cleanup → run): the cleanup
  // clears the timer but the immediate re-run re-arms it, unlike the
  // previous single-effect version whose re-run early-returned on the ref
  // check and left the banner stuck on screen.
  useEffect(() => {
    if (!visible) return;
    const t = setTimeout(() => setVisible(false), TOTAL_MS);
    return () => clearTimeout(t);
  }, [visible]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="deal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            pointerEvents: 'none',
            zIndex: 9500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(circle at center, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%)',
          }}
          aria-hidden
        >
          {/* Card burst */}
          {Array.from({ length: NUM_CARDS }).map((_, i) => {
            const angle = (i / NUM_CARDS) * 360;
            const rad = (angle * Math.PI) / 180;
            const radius = 260;
            const tx = Math.cos(rad) * radius;
            const ty = Math.sin(rad) * radius;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 0, y: 0, scale: 0.4, rotate: 0 }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  x: [0, tx * 0.5, tx],
                  y: [0, ty * 0.5, ty],
                  scale: [0.4, 1, 0.9],
                  rotate: [0, angle + 30, angle + 60],
                }}
                transition={{
                  duration: TOTAL_MS / 1000,
                  delay: (i % 7) * 0.04,
                  ease: [0.4, 0, 0.2, 1],
                  times: [0, 0.35, 0.9, 1],
                }}
                style={{
                  position: 'absolute',
                  width: 52,
                  height: 72,
                  marginLeft: -26,
                  marginTop: -36,
                  top: '50%',
                  left: '50%',
                  borderRadius: 8,
                  background:
                    'repeating-linear-gradient(45deg, rgba(255,255,255,0.08) 0 4px, transparent 4px 8px),' +
                    'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)',
                  border: '2px solid rgba(255, 255, 255, 0.35)',
                  boxShadow: '0 6px 24px rgba(0,0,0,0.6), 0 0 12px rgba(239, 68, 68, 0.4)',
                }}
              />
            );
          })}

          {/* Hand label - the part that makes it unmistakable */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            style={{
              position: 'relative',
              zIndex: 2,
              background: 'rgba(13, 21, 33, 0.92)',
              border: '2px solid rgba(56, 189, 248, 0.6)',
              boxShadow: '0 0 0 4px rgba(56, 189, 248, 0.15), 0 12px 36px rgba(0, 0, 0, 0.6)',
              borderRadius: 16,
              padding: '14px 28px',
              textAlign: 'center',
              color: '#fff',
              backdropFilter: 'blur(6px)',
            }}
          >
            <div style={{
              fontSize: 22,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: 2,
            }}>
              NEW HAND
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DealAnimation;
